import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { CELL } from '../core/config';
import { occupiedBoxes, type Vec } from '../destruction/structure';
import type { DormantCluster } from './world';
import type { FlatSupport } from './foot-clearance';
import type { Cell } from '../world/city';

/** Conservative local occupied bounds, with optional reconstruction yields. */
export function* occupiedBoundsSteps(
  cells: Iterable<Cell>,
  origin: Vec,
  batch = Infinity,
): Generator<void, THREE.Box3> {
  const bounds = new THREE.Box3();
  let count = 0;
  for (const cell of cells) {
    const b = cell.bounds,
      x = (cell.x + (b?.x ?? 0)) * CELL - origin.x,
      y = (cell.y + (b?.y ?? 0)) * CELL - origin.y,
      z = (cell.z + (b?.z ?? 0)) * CELL - origin.z;
    bounds.min.x = Math.min(bounds.min.x, x);
    bounds.min.y = Math.min(bounds.min.y, y);
    bounds.min.z = Math.min(bounds.min.z, z);
    bounds.max.x = Math.max(bounds.max.x, x + (b?.w ?? 1) * CELL);
    bounds.max.y = Math.max(bounds.max.y, y + (b?.h ?? 1) * CELL);
    bounds.max.z = Math.max(bounds.max.z, z + (b?.d ?? 1) * CELL);
    if (++count % batch === 0) yield;
  }
  return bounds;
}

interface OccupiedShape {
  bounds: THREE.Box3;
  position: THREE.Vector3;
  shape: RAPIER.Cuboid;
}
interface ShapeNode {
  bounds: THREE.Box3;
  shapes?: OccupiedShape[];
  left?: ShapeNode;
  right?: ShapeNode;
}
function shapeTree(shapes: OccupiedShape[]): ShapeNode {
  const bounds = new THREE.Box3();
  for (const shape of shapes) bounds.union(shape.bounds);
  if (shapes.length <= 6) return { bounds, shapes };
  const span = bounds.getSize(new THREE.Vector3()),
    axis = span.x >= span.y && span.x >= span.z ? 'x' : span.y >= span.z ? 'y' : 'z';
  shapes.sort((a, b) => a.bounds.min[axis] + a.bounds.max[axis] - b.bounds.min[axis] - b.bounds.max[axis]);
  const middle = shapes.length >> 1;
  return { bounds, left: shapeTree(shapes.slice(0, middle)), right: shapeTree(shapes.slice(middle)) };
}
interface Entry {
  record: DormantCluster;
  bounds: THREE.Box3;
  tiles: string[];
}
// Foot probes are local. Smaller bins avoid scanning unrelated floors and
// furnishings across an entire city block; exact occupied tests are unchanged.
const TILE = 8;
const MAX_CACHED_SHAPES = 262144;
const MAX_CACHED_RECORDS = 8192;
const IDENTITY = { x: 0, y: 0, z: 0, w: 1 };

/** Sleeping streamed-out geometry remains occupied without recreating physics bodies. */
export class DormantNavigation {
  private entries = new Map<number, Entry>();
  private tiles = new Map<string, Set<number>>();
  private cache = new Map<number, OccupiedShape[]>();
  private cachedShapes = 0;
  private shapeTrees = new WeakMap<OccupiedShape[], ShapeNode>();
  groundCalls = 0;
  groundCandidates = 0;
  shapeIndexBuilds = 0;
  shapeIndexQueries = 0;

  bounds(id: number): THREE.Box3 | undefined {
    return this.entries.get(id)?.bounds;
  }

  clear(): void {
    this.entries.clear();
    this.tiles.clear();
    this.cache.clear();
    this.cachedShapes = 0;
    this.shapeTrees = new WeakMap();
    this.groundCalls = this.groundCandidates = 0;
    this.shapeIndexBuilds = this.shapeIndexQueries = 0;
  }
  private transform(record: DormantCluster): THREE.Matrix4 {
    const { position: p, rotation: q } = record;
    return new THREE.Matrix4().compose(
      new THREE.Vector3(p.x, p.y, p.z),
      new THREE.Quaternion(q.x, q.y, q.z, q.w),
      new THREE.Vector3(1, 1, 1),
    );
  }
  add(record: DormantCluster, localBounds?: THREE.Box3): void {
    this.remove(record.id);
    if (!record.cells.size) return;
    let bounds = localBounds?.clone();
    if (!bounds) {
      const task = occupiedBoundsSteps(record.cells.values(), record.origin);
      let result = task.next();
      while (!result.done) result = task.next();
      bounds = result.value;
    }
    bounds.applyMatrix4(this.transform(record));
    const tiles: string[] = [];
    for (let x = Math.floor(bounds.min.x / TILE); x <= Math.floor(bounds.max.x / TILE); x++)
      for (let z = Math.floor(bounds.min.z / TILE); z <= Math.floor(bounds.max.z / TILE); z++) {
        const key = `${x},${z}`;
        let ids = this.tiles.get(key);
        if (!ids) this.tiles.set(key, (ids = new Set()));
        ids.add(record.id);
        tiles.push(key);
      }
    this.entries.set(record.id, { record, bounds, tiles });
  }
  remove(id: number): void {
    const entry = this.entries.get(id);
    if (entry)
      for (const key of entry.tiles) {
        const ids = this.tiles.get(key)!;
        ids.delete(id);
        if (!ids.size) this.tiles.delete(key);
      }
    this.entries.delete(id);
    const shapes = this.cache.get(id);
    if (shapes) this.cachedShapes -= shapes.length;
    this.cache.delete(id);
  }
  /** Broad phase over actual occupied bounds, including transformed rubble. */
  queryBounds(bounds: THREE.Box3, visit: (record: DormantCluster, bounds: THREE.Box3) => void): void {
    const seen = new Set<number>();
    for (let x = Math.floor(bounds.min.x / TILE); x <= Math.floor(bounds.max.x / TILE); x++)
      for (let z = Math.floor(bounds.min.z / TILE); z <= Math.floor(bounds.max.z / TILE); z++)
        for (const id of this.tiles.get(`${x},${z}`) ?? []) {
          if (seen.has(id)) continue;
          seen.add(id);
          const entry = this.entries.get(id)!;
          if (entry.bounds.intersectsBox(bounds)) visit(entry.record, entry.bounds);
        }
  }
  private occupied(record: DormantCluster): OccupiedShape[] {
    const existing = this.cache.get(record.id);
    if (existing) {
      this.cache.delete(record.id);
      this.cache.set(record.id, existing);
      return existing;
    }
    const boxes = occupiedBoxes(record.cells.values());
    const transform = this.transform(record);
    const shapes = boxes.map((b): OccupiedShape => {
      const min = new THREE.Vector3(
        b.x * CELL - record.origin.x,
        b.y * CELL - record.origin.y,
        b.z * CELL - record.origin.z,
      );
      const max = min.clone().add(new THREE.Vector3(b.w * CELL, b.h * CELL, b.d * CELL));
      const bounds = new THREE.Box3(min, max);
      const position = bounds.getCenter(new THREE.Vector3()).applyMatrix4(transform);
      bounds.applyMatrix4(transform);
      return {
        bounds,
        position,
        shape: new RAPIER.Cuboid((b.w * CELL) / 2, (b.h * CELL) / 2, (b.d * CELL) / 2),
      };
    });
    // LRU limits retained query shapes independently of the world's cell count.
    if (shapes.length <= MAX_CACHED_SHAPES) {
      while (this.cache.size >= MAX_CACHED_RECORDS || this.cachedShapes + shapes.length > MAX_CACHED_SHAPES) {
        const id = this.cache.keys().next().value!;
        this.cachedShapes -= this.cache.get(id)!.length;
        this.cache.delete(id);
      }
      this.cache.set(record.id, shapes);
      this.cachedShapes += shapes.length;
    }
    return shapes;
  }
  private nearby(
    record: DormantCluster,
    minY: number,
    maxY: number,
    minX: number,
    maxX: number,
    minZ: number,
    maxZ: number,
  ): OccupiedShape[] {
    const shapes = this.occupied(record);
    if (shapes.length < 48) return shapes;
    let root = this.shapeTrees.get(shapes);
    if (!root) {
      // The old height bands still scanned every cuboid across an entire
      // floor for each footstep. Index the same immutable occupied bounds in
      // all three dimensions. Each shape occurs once, including long slabs.
      root = shapeTree(shapes.slice());
      this.shapeTrees.set(shapes, root);
      this.shapeIndexBuilds++;
    }
    this.shapeIndexQueries++;
    const result: OccupiedShape[] = [],
      pending = [root];
    while (pending.length) {
      const node = pending.pop()!,
        b = node.bounds;
      if (
        b.min.x > maxX ||
        b.max.x < minX ||
        b.min.y > maxY ||
        b.max.y < minY ||
        b.min.z > maxZ ||
        b.max.z < minZ
      )
        continue;
      if (node.shapes) result.push(...node.shapes);
      else pending.push(node.right!, node.left!);
    }
    return result;
  }
  blocked(point: Vec, radius: number, halfHeight = 0.68): boolean {
    const bounds = new THREE.Box3(
      new THREE.Vector3(point.x - radius, point.y - halfHeight, point.z - radius),
      new THREE.Vector3(point.x + radius, point.y + halfHeight, point.z + radius),
    );
    const seen = new Set<number>();
    let query: RAPIER.Cuboid | undefined;
    for (let x = Math.floor(bounds.min.x / TILE); x <= Math.floor(bounds.max.x / TILE); x++)
      for (let z = Math.floor(bounds.min.z / TILE); z <= Math.floor(bounds.max.z / TILE); z++) {
        const ids = this.tiles.get(`${x},${z}`);
        if (!ids) continue;
        for (const id of ids) {
          if (seen.has(id)) continue;
          seen.add(id);
          const entry = this.entries.get(id)!;
          if (!entry.bounds.intersectsBox(bounds)) continue;
          for (const shape of this.nearby(
            entry.record,
            bounds.min.y,
            bounds.max.y,
            bounds.min.x,
            bounds.max.x,
            bounds.min.z,
            bounds.max.z,
          )) {
            if (!shape.bounds.intersectsBox(bounds)) continue;
            query ??= new RAPIER.Cuboid(radius, halfHeight, radius);
            if (shape.shape.intersectsShape(shape.position, entry.record.rotation, query, point, IDENTITY))
              return true;
          }
        }
      }
    return false;
  }
  /** Exact occupied-volume query for a reserved actor pose. */
  intersectsShape(
    point: Vec,
    probe: RAPIER.Shape,
    rotation: { x: number; y: number; z: number; w: number },
    extent: number,
    excludeBuilding?: number,
  ): boolean {
    let hit = false;
    const bounds = new THREE.Box3(
      new THREE.Vector3(point.x, point.y, point.z),
      new THREE.Vector3(point.x, point.y, point.z),
    ).expandByScalar(extent);
    this.queryBounds(bounds, (record) => {
      if (hit || record.building.id === excludeBuilding) return;
      for (const shape of this.nearby(
        record,
        bounds.min.y,
        bounds.max.y,
        bounds.min.x,
        bounds.max.x,
        bounds.min.z,
        bounds.max.z,
      )) {
        if (
          shape.bounds.intersectsBox(bounds) &&
          shape.shape.intersectsShape(shape.position, record.rotation, probe, point, rotation)
        ) {
          hit = true;
          break;
        }
      }
    });
    return hit;
  }
  /** Exact dormant targeting. A 2D grid traversal only visits tiles crossed by the ray. */
  ray(origin: Vec, direction: Vec, length: number): { owner: number; time: number; normal: Vec } | undefined {
    const ray = new RAPIER.Ray(origin, direction),
      threeRay = new THREE.Ray(
        new THREE.Vector3(origin.x, origin.y, origin.z),
        new THREE.Vector3(direction.x, direction.y, direction.z),
      );
    const near = new THREE.Vector3(),
      seen = new Set<number>();
    let x = Math.floor(origin.x / TILE),
      z = Math.floor(origin.z / TILE),
      t = 0,
      best = length;
    const sx = Math.sign(direction.x),
      sz = Math.sign(direction.z),
      dx = sx ? TILE / Math.abs(direction.x) : Infinity,
      dz = sz ? TILE / Math.abs(direction.z) : Infinity;
    let tx = sx ? ((x + (sx > 0 ? 1 : 0)) * TILE - origin.x) / direction.x : Infinity,
      tz = sz ? ((z + (sz > 0 ? 1 : 0)) * TILE - origin.z) / direction.z : Infinity;
    let result: { owner: number; time: number; normal: Vec } | undefined;
    while (t <= best) {
      for (const id of this.tiles.get(`${x},${z}`) ?? []) {
        if (seen.has(id)) continue;
        seen.add(id);
        const entry = this.entries.get(id)!;
        if (!threeRay.intersectBox(entry.bounds, near) || near.distanceTo(threeRay.origin) > best) continue;
        for (const shape of this.occupied(entry.record)) {
          if (!threeRay.intersectBox(shape.bounds, near) || near.distanceTo(threeRay.origin) > best) continue;
          const hit = shape.shape.castRayAndGetNormal(ray, shape.position, entry.record.rotation, best, true);
          if (hit && hit.timeOfImpact <= best) {
            best = hit.timeOfImpact;
            result = { owner: id, time: best, normal: hit.normal };
          }
        }
      }
      if (!Number.isFinite(Math.min(tx, tz))) break;
      if (tx < tz) {
        t = tx;
        tx += dx;
        x += sx;
      } else if (tz < tx) {
        t = tz;
        tz += dz;
        z += sz;
      } else {
        t = tx;
        tx += dx;
        tz += dz;
        x += sx;
        z += sz;
      }
    }
    return result;
  }
  ground(point: Vec, up: number, down: number): { y: number; owner: number; normal: Vec } | undefined {
    this.groundCalls++;
    const ray = new RAPIER.Ray({ x: point.x, y: point.y + up, z: point.z }, { x: 0, y: -1, z: 0 });
    let best = up + down,
      result: { y: number; owner: number; normal: Vec } | undefined;
    for (const id of this.tiles.get(`${Math.floor(point.x / TILE)},${Math.floor(point.z / TILE)}`) ?? []) {
      const entry = this.entries.get(id)!;
      if (
        point.x < entry.bounds.min.x ||
        point.x > entry.bounds.max.x ||
        point.z < entry.bounds.min.z ||
        point.z > entry.bounds.max.z ||
        entry.bounds.min.y > point.y + up ||
        entry.bounds.max.y < point.y - down
      )
        continue;
      for (const shape of this.nearby(
        entry.record,
        point.y - down,
        point.y + up,
        point.x,
        point.x,
        point.z,
        point.z,
      )) {
        this.groundCandidates++;
        const b = shape.bounds;
        if (
          point.x < b.min.x ||
          point.x > b.max.x ||
          point.z < b.min.z ||
          point.z > b.max.z ||
          b.min.y > point.y + up ||
          b.max.y < point.y + up - best
        )
          continue;
        const q = entry.record.rotation,
          sin = 2 * q.y * q.w;
        if (
          Math.abs(q.x) < 1e-8 &&
          Math.abs(q.z) < 1e-8 &&
          (Math.abs(sin) < 1e-7 || Math.abs(Math.abs(sin) - 1) < 1e-7)
        ) {
          const t = point.y + up - b.max.y;
          if (t >= -1e-7 && t <= best) {
            best = Math.max(0, t);
            result = { y: b.max.y, owner: id, normal: { x: 0, y: 1, z: 0 } };
          }
          continue;
        }
        const hit = shape.shape.castRayAndGetNormal(ray, shape.position, entry.record.rotation, best, false);
        if (hit && hit.timeOfImpact <= best && hit.normal.y > 0.65) {
          best = hit.timeOfImpact;
          result = { y: point.y + up - best, owner: id, normal: hit.normal };
        }
      }
    }
    return result;
  }
  sweepCapsule(point: Vec, move: Vec, radius: number): { time: number; normal: THREE.Vector3 } | undefined {
    return this.sweepShape(point, move, new RAPIER.Capsule(0.58, radius), IDENTITY, {
      x: radius,
      y: 0.58 + radius,
      z: radius,
    });
  }
  flatSupport(owner: number, point: Vec, y: number): FlatSupport | undefined {
    const entry = this.entries.get(owner);
    if (!entry || (!entry.record.anchored && !entry.record.settled)) return;
    const q = entry.record.rotation,
      sin = 2 * q.y * q.w;
    if (
      Math.abs(q.x) > 1e-8 ||
      Math.abs(q.z) > 1e-8 ||
      (Math.abs(sin) > 1e-7 && Math.abs(Math.abs(sin) - 1) > 1e-7)
    )
      return;
    for (const shape of this.nearby(
      entry.record,
      y - 0.00005,
      y + 0.00005,
      point.x,
      point.x,
      point.z,
      point.z,
    )) {
      const b = shape.bounds;
      if (
        Math.abs(b.max.y - y) < 0.00005 &&
        point.x >= b.min.x &&
        point.x <= b.max.x &&
        point.z >= b.min.z &&
        point.z <= b.max.z
      )
        return { owner, y, minX: b.min.x, maxX: b.max.x, minZ: b.min.z, maxZ: b.max.z };
    }
  }
  sweepShape(
    point: Vec,
    move: Vec,
    probe: RAPIER.Shape,
    rotation: { x: number; y: number; z: number; w: number },
    extent: Vec,
    margin = 0.015,
  ): { time: number; normal: THREE.Vector3 } | undefined {
    const box = new THREE.Box3(
      new THREE.Vector3(point.x, point.y, point.z),
      new THREE.Vector3(point.x, point.y, point.z),
    );
    box.expandByPoint(new THREE.Vector3(point.x + move.x, point.y + move.y, point.z + move.z));
    box.expandByVector(new THREE.Vector3(extent.x, extent.y, extent.z));
    const seen = new Set<number>();
    let best: { time: number; normal: THREE.Vector3 } | undefined;
    for (let x = Math.floor(box.min.x / TILE); x <= Math.floor(box.max.x / TILE); x++)
      for (let z = Math.floor(box.min.z / TILE); z <= Math.floor(box.max.z / TILE); z++)
        for (const id of this.tiles.get(`${x},${z}`) ?? []) {
          if (seen.has(id)) continue;
          seen.add(id);
          const entry = this.entries.get(id)!;
          if (!entry.bounds.intersectsBox(box)) continue;
          for (const shape of this.nearby(
            entry.record,
            box.min.y,
            box.max.y,
            box.min.x,
            box.max.x,
            box.min.z,
            box.max.z,
          )) {
            if (!shape.bounds.intersectsBox(box)) continue;
            const hit = shape.shape.castShape(
              shape.position,
              entry.record.rotation,
              { x: 0, y: 0, z: 0 },
              probe,
              point,
              rotation,
              move,
              margin,
              best?.time ?? 1,
              false,
            );
            if (hit && hit.time_of_impact < (best?.time ?? 1))
              best = {
                time: hit.time_of_impact,
                normal: new THREE.Vector3(hit.normal1.x, hit.normal1.y, hit.normal1.z).applyQuaternion(
                  new THREE.Quaternion(
                    entry.record.rotation.x,
                    entry.record.rotation.y,
                    entry.record.rotation.z,
                    entry.record.rotation.w,
                  ),
                ),
              };
          }
        }
    return best;
  }
}
